<script>
import { Annotation } from '@/api';
import WKT from 'ol/format/WKT';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Text } from 'ol/style';
import vectorLayer from 'vuelayers/lib/mixin/vector-layer';

const PARASITE_COLOR = '#23d160';
const WBC_COLOR = '#3e8ed0';
const PARASITE_FILL = 'rgba(35, 209, 96, 0.08)';
const WBC_FILL = 'rgba(62, 142, 208, 0.08)';
const USER_ANNOTATION_CLASS = 'be.cytomine.ontology.UserAnnotation';

export default {
  name: 'malaria-detection-overlay-layer',
  mixins: [vectorLayer],
  props: {
    annotations: {
      type: Array,
      default: () => []
    },
    points: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      format: new WKT()
    };
  },
  methods: {
    createLayer() {
      this.overlaySource = new VectorSource({
        features: this.createFeatures(this.overlayItems())
      });

      return new VectorLayer({
        id: this.id,
        source: this.overlaySource,
        visible: this.visible,
        opacity: this.opacity,
        extent: this.extent,
        zIndex: this.zIndex,
        style: this.styleFeature
      });
    },
    overlayItems() {
      return this.annotations && this.annotations.length ? this.annotations : this.points;
    },
    decodedLocation(item) {
      if (!item.location) {
        return null;
      }
      if (!item.geometryCompression) {
        return item.location;
      }
      try {
        return decodeURIComponent(atob(item.location));
      } catch (error) {
        return item.location;
      }
    },
    featureForItem(item) {
      const location = this.decodedLocation(item);
      if (!location) {
        return null;
      }
      let feature;
      try {
        feature = this.format.readFeature(location);
      } catch (error) {
        return null;
      }
      const annot = this.annotationForItem(item, location, feature);
      feature.setId(annot.id);
      feature.set('annot', annot);
      feature.set('id', annot.id);
      feature.set('type', item.type);
      feature.set('confidence', Number(item.confidence) || 0);
      feature.set('showConfidenceLabel', item.showConfidenceLabel !== false);
      return feature;
    },
    annotationForItem(item, location, feature) {
      const extent = feature.getGeometry().getExtent();
      const centroid = item.centroid || {
        x: (extent[0] + extent[2]) / 2,
        y: (extent[1] + extent[3]) / 2
      };
      const timestamp = Number(item.created) || Number(item.updated) || Date.now();
      const annot = new Annotation({
        id: item.id,
        class: item.class || USER_ANNOTATION_CLASS,
        created: timestamp,
        updated: Number(item.updated) || timestamp,
        project: item.project,
        image: item.image,
        slice: item.slice,
        user: item.user,
        location,
        geometryCompression: false,
        centroid,
        area: item.area,
        areaUnit: item.areaUnit,
        perimeter: item.perimeter,
        perimeterUnit: item.perimeterUnit,
        term: item.term || []
      });
      annot.confidence = item.confidence;
      annot.aiVisualizationType = item.aiVisualizationType;
      annot.malariaDetectionType = item.type;
      return annot;
    },
    createFeatures(items) {
      return (items || [])
        .filter(item => item.visible !== false)
        .map(item => this.featureForItem(item))
        .filter(Boolean);
    },
    styleFeature(feature) {
      const type = feature.get('type');
      const isWbc = ['wbc', 'white_blood_cell', 'white blood cell'].includes(type);
      const color = isWbc ? WBC_COLOR : PARASITE_COLOR;
      const fill = isWbc ? WBC_FILL : PARASITE_FILL;
      const label = Number(feature.get('confidence') || 0).toFixed(2);
      const baseStyle = new Style({
        stroke: new Stroke({ color, width: 2 }),
        fill: new Fill({ color: fill })
      });

      if (feature.get('showConfidenceLabel') === false) {
        return [baseStyle];
      }

      return [
        baseStyle,
        new Style({
          geometry: this.labelGeometry,
          text: new Text({
            text: label,
            offsetY: -8,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 3 }),
            font: '700 12px sans-serif',
            overflow: true,
            textAlign: 'center',
            textBaseline: 'bottom'
          })
        })
      ];
    },
    labelGeometry(feature) {
      const extent = feature.getGeometry().getExtent();
      return new Point([
        (extent[0] + extent[2]) / 2,
        extent[3]
      ]);
    },
    refreshFeatures() {
      if (!this.overlaySource) {
        return;
      }
      this.overlaySource.clear(true);
      this.overlaySource.addFeatures(this.createFeatures(this.overlayItems()));
    }
  },
  watch: {
    annotations: {
      deep: true,
      handler() {
        this.refreshFeatures();
      }
    },
    points: {
      deep: true,
      handler() {
        this.refreshFeatures();
      }
    }
  }
};
</script>
